/**
 * Risk Assessment tab — per-domain risk levels + mitigation notes.
 * Singleton record per client; PUT upserts.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, AlertTriangle } from "lucide-react";

const LEVELS = [
  { v: "", l: "Not assessed", color: "#94A3B8" },
  { v: "low", l: "Low", color: "#22C55E" },
  { v: "moderate", l: "Moderate", color: "#F59E0B" },
  { v: "high", l: "High", color: "#EF4444" },
];

const DOMAINS = [
  { k: "fall_risk", mk: "fall_mitigations", l: "Falls" },
  { k: "elopement_risk", mk: "elopement_mitigations", l: "Elopement / wandering" },
  { k: "choking_aspiration_risk", mk: "choking_mitigations", l: "Choking / aspiration" },
  { k: "self_harm_risk", mk: "self_harm_mitigations", l: "Self-harm" },
  { k: "suicide_risk", mk: "suicide_mitigations", l: "Suicide" },
  { k: "aggression_risk", mk: "aggression_mitigations", l: "Aggression toward others" },
  { k: "medication_self_admin_risk", mk: "medication_mitigations", l: "Medication self-admin" },
  { k: "skin_breakdown_risk", mk: "skin_mitigations", l: "Skin breakdown" },
  { k: "infection_risk", mk: "infection_mitigations", l: "Infection" },
  { k: "abuse_neglect_risk", mk: "abuse_mitigations", l: "Abuse / neglect" },
  { k: "financial_exploitation_risk", mk: "financial_mitigations", l: "Financial exploitation" },
];

export default function ClientRiskAssessmentTab({ clientId }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/clients/${clientId}/risk-assessment`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/risk-assessment`, data);
      toast.success("Risk assessment saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const highCount = DOMAINS.filter((d) => data[d.k] === "high").length;

  return (
    <div className="space-y-4" data-testid="client-risk-assessment-tab">
      <div className="card-surface p-4 flex items-center justify-between">
        <div>
          <div className="label-eyebrow">Overall risk</div>
          <select
            value={data.overall_risk || ""}
            onChange={(e) => set("overall_risk", e.target.value)}
            className="mt-1 rounded-lg border border-clinical-border bg-white px-3 py-1.5 text-sm font-medium"
            aria-label="Overall risk level"
            data-testid="risk-overall"
          >
            {LEVELS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
          </select>
        </div>
        {highCount > 0 && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertTriangle size={14} />
            {highCount} high-risk domain{highCount === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <div className="card-surface p-4 space-y-3">
        <div className="label-eyebrow">Review</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="risk-review-due" className="text-xs text-clinical-subtext">Review due on</label>
            <input
              id="risk-review-due"
              type="date"
              value={data.review_due_on || ""}
              onChange={(e) => set("review_due_on", e.target.value)}
              className="input-clinical mt-1"
              data-testid="risk-review-due"
            />
          </div>
          {data.updated_at && (
            <div className="text-xs text-clinical-subtext self-end pb-2">
              Last updated: {new Date(data.updated_at).toLocaleDateString()}
              {data.updated_by_name && ` by ${data.updated_by_name}`}
            </div>
          )}
        </div>
        <div>
          <label htmlFor="risk-summary" className="text-xs text-clinical-subtext">Assessment summary</label>
          <textarea
            id="risk-summary"
            rows={2}
            value={data.summary || ""}
            onChange={(e) => set("summary", e.target.value)}
            placeholder="High-level summary the on-call team can read at a glance…"
            className="input-clinical resize-y mt-1"
            data-testid="risk-summary"
          />
        </div>
      </div>

      <div className="card-surface p-4 space-y-3">
        {DOMAINS.map((d) => {
          const level = data[d.k] || "";
          const meta = LEVELS.find((l) => l.v === level) || LEVELS[0];
          return (
            <div key={d.k} className="border-b border-clinical-border/40 pb-3 last:border-0 last:pb-0">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-clinical-text">{d.l}</span>
                <select
                  value={level}
                  onChange={(e) => set(d.k, e.target.value)}
                  className="rounded-lg border bg-white px-2 py-1 text-xs font-medium"
                  style={{ borderColor: meta.color, color: meta.color }}
                  aria-label={`${d.l} risk level`}
                  data-testid={`risk-${d.k}`}
                >
                  {LEVELS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
                </select>
              </div>
              {(level === "moderate" || level === "high") && (
                <textarea
                  rows={2}
                  value={data[d.mk] || ""}
                  onChange={(e) => set(d.mk, e.target.value)}
                  placeholder={`Mitigation plan for ${d.l.toLowerCase()}…`}
                  className="input-clinical resize-y text-xs"
                  aria-label={`${d.l} mitigation plan`}
                  data-testid={`risk-${d.mk}`}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-0 bg-white pt-3">
        <button type="button" onClick={save} disabled={saving} className="btn-primary"
          data-testid="risk-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save risk assessment
        </button>
      </div>
    </div>
  );
}
